import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { isSuperAdmin } from '../lib/adminRoles';
import { getAdminUsers, createAdminUser, deleteAdminUser } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Trash2, UserPlus } from 'lucide-react';

export default function AdminManageAdmins() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const load = async () => {
    try {
      const rows = await getAdminUsers();
      setList(Array.isArray(rows) ? rows : []);
    } catch {
      toast.error('No se pudo cargar la lista de administradores.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!authLoading && user && !isSuperAdmin(user.role)) {
      toast.error('Solo el super administrador puede gestionar cuentas.');
      navigate('/admin', { replace: true });
    }
  }, [authLoading, user, navigate]);

  useEffect(() => {
    if (user && isSuperAdmin(user.role)) load();
  }, [user]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await createAdminUser({ name, email, password });
      toast.success('Administrador creado. Inicia sesión con su correo y contraseña.');
      setName('');
      setEmail('');
      setPassword('');
      await load();
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'No se pudo crear el usuario.');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id, role) => {
    if (role !== 'admin') {
      toast.error('No se puede eliminar desde aquí.');
      return;
    }
    if (!window.confirm('¿Eliminar este administrador? No podrá volver a entrar al panel.')) return;
    try {
      await deleteAdminUser(id);
      toast.success('Usuario eliminado.');
      await load();
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'No se pudo eliminar.');
    }
  };

  if (authLoading || (user && isSuperAdmin(user.role) && loading)) {
    return <div className="text-muted-foreground animate-pulse">Cargando administradores…</div>;
  }

  return (
    <div className="max-w-3xl space-y-8" data-testid="admin-manage-admins">
      <div>
        <h1 className="font-heading text-2xl font-bold text-foreground">Administradores</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Crea cuentas con rol <strong className="font-medium text-foreground">admin</strong> para el día a día. El rol{' '}
          <strong className="font-medium text-foreground">super_admin</strong> solo se define en el servidor.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <UserPlus className="w-5 h-5" />
            Nuevo administrador
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleCreate} className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="new_name">Nombre</Label>
              <Input id="new_name" value={name} onChange={(e) => setName(e.target.value)} required minLength={1} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new_email">Correo</Label>
              <Input id="new_email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>
            <div className="sm:col-span-2 space-y-2">
              <Label htmlFor="new_password">Contraseña (mín. 6 caracteres)</Label>
              <Input
                id="new_password"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
              />
            </div>
            <div className="sm:col-span-2">
              <Button type="submit" disabled={creating} className="rounded-full">
                {creating ? 'Creando…' : 'Crear administrador'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Cuentas existentes</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="divide-y divide-border">
            {list.map((u) => (
              <li key={u.id} className="flex flex-wrap items-center justify-between gap-3 py-4 first:pt-0">
                <div>
                  <p className="font-medium text-foreground">{u.name}</p>
                  <p className="text-sm text-muted-foreground">{u.email}</p>
                  {(u.username || u.role === 'super_admin') && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {u.role === 'super_admin' ? 'Rol: super_admin' : `Usuario: ${u.username}`}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={`text-xs px-2 py-1 rounded-full ${
                      u.role === 'super_admin' ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    {u.role}
                  </span>
                  {u.role === 'admin' && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="text-destructive"
                      onClick={() => handleDelete(u.id, u.role)}
                      aria-label={`Eliminar ${u.email}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
